import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import fileUpload from "../../images/file-upload.svg";
import DeviceOnboardingService from './DeviceOnboardingService';
import { useSelector, useDispatch } from 'react-redux';
import { getDeviceTypeInit, getDeviceOnboardingLogsInit, getProcessNamesInit } from './redux/actions';
import DeviceOnboardingLogs from './DeviceOnboardingLogs';
import './DeviceOnboarding.scss';

function DeviceOnboarding() {

    const fileInputRef = useRef(null);
    const [selectedDeviceType, setSelectedDeviceType] = useState(null);
    const [selectedFile, setSelectedFile] = useState();
    const [selectedFileName, setSelectedFileName] = useState();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [resultMessage, setResultMessage] = useState(null);
    const [showResultMessage, setShowResultMessage] = useState(false);
    const [fileUploadError, setFileUploadError] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);
    const [snowTicketNumber, setSnowTicketNumber] = useState("");
    const [description, setDescription] = useState("");
    const [selectedProcessName, setSelectedProcessName] = useState(null);


    const deviceService = useMemo(() => new DeviceOnboardingService(), []);
    const [downloadTemplateUrl, setDownloadTemplateUrl] = useState('');

    /**
    * Fetch the On Boarding Device types and list it into Device Type dropdown field
    */
    const deviceTypes = useSelector(state => state.deviceOnboarding.deviceTypeList);
    console.log("Device Types from Redux Store:", deviceTypes); // Debug log to check the fetched device types
    const processNames = useSelector(state => state.deviceOnboarding.processNames);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getDeviceTypeInit());
        dispatch(getProcessNamesInit());
    }, [dispatch]);

    // Auto-select the first device type once they load
    useEffect(() => {
        if (deviceTypes?.length > 0 && !selectedDeviceType) {
            setSelectedDeviceType(deviceTypes[0]);
        }
    }, [deviceTypes, selectedDeviceType]);

    // Default / reset process name whenever the selected device type changes
    useEffect(() => {
        if (selectedDeviceType?.deviceTypeId === 17 && processNames?.length > 0) {
            setSelectedProcessName(processNames[0].value);
        } else {
            setSelectedProcessName(null);
        }
    }, [selectedDeviceType, processNames]);

    // Fetch template URL whenever device type or process name changes (race-safe)
    useEffect(() => {
        if (!selectedDeviceType) {
            setDownloadTemplateUrl('');
            return undefined;
        }
        let cancelled = false;
        const processNameParam =
            selectedDeviceType.deviceTypeId === 17 ? (selectedProcessName || '') : '';
        deviceService.getTemplateUrl(selectedDeviceType.name, processNameParam)
            .then(url => { if (!cancelled) setDownloadTemplateUrl(url); })
            .catch(() => { if (!cancelled) setDownloadTemplateUrl(''); });
        return () => { cancelled = true; };
    }, [selectedDeviceType, selectedProcessName, deviceService]);

    /**
    * To bind the selected dropdown value to Device Type field
    */
    const deviceTypeChange = (e) => setSelectedDeviceType(e.value);

    /**
    * To select the Device Bulk upload Excel file
    * @param {Object} event - Selected file
    */
    const onFileSelect = (event) => {
        const fileName = event.files[0].name;
        const fileSize = event.files[0].size;
        const MAX_FILE_SIZE = 2*1024*1024;
        if ((fileName.toLowerCase().endsWith('.xls') || fileName.toLowerCase().endsWith('.xlsx')) && fileSize <= MAX_FILE_SIZE) {
            setSelectedFile(event.files[0]);
            setSelectedFileName(event.files[0].name);
            return true;
        }
        fileInputRef.current.clear();
        setDisplayDialog(true);
        return false;
    }

    /**
    * Upload the selected Device Bulk upload file into database
    */
    const onFileUpload = () => {
        if(selectedDeviceType.deviceTypeId === 10
            && (!snowTicketNumber || snowTicketNumber.trim().length === 0)) {
            setFileUploadError(["Snow Ticket # cannot be empty"]);
        }
        else if(selectedDeviceType.deviceTypeId === 10
            && (!description || description.trim().length === 0)) {
            setFileUploadError(["Description cannot be empty"]);
        } else if(selectedDeviceType.deviceTypeId === 10 && description.length>150) {
            setFileUploadError(["Description cannot have more than 150 characters"]);
        } else if(selectedDeviceType.deviceTypeId === 17 && !selectedProcessName) {
            setFileUploadError(["Process Name must be selected"]);
        } else {
            setFileUploadError([]);
            const formData = new FormData();
            formData.append('file', selectedFile);
    
            const processNameValue = selectedDeviceType.deviceTypeId === 17 ? selectedProcessName : '';
            deviceService.bulkUpload(formData, selectedDeviceType.deviceTypeId, snowTicketNumber.trim(), description.trim(), processNameValue).then(res => {
                if (res) {
                    dispatch(getDeviceOnboardingLogsInit());
                    setSuccessMessage(res.data);
                    setResultMessage('Uploaded Successfully');
                    setFileUploadError([]);
                    setShowResultMessage(true);
                    // Clear the file upload component to hide p-fileupload-content
                    fileInputRef.current.clear();
                    // setSelectedFile(null);
                    // setSelectedFileName(null);
                    setTimeout(() => {
                        setResultMessage('');
                        setShowResultMessage(false);
                    }, 5000);
                }
            }).catch(error => {
                setFileUploadError(error?.response?.data?.detailList);
                setResultMessage('Uploaded Failed');
                setShowResultMessage(true);
                setTimeout(() => {
                    setResultMessage('');
                    setShowResultMessage(false);
                }, 5000);
            });
        }
    }

    return (
        <div className="device-onboarding-container container-fluid">
            <p className="page-label">
				Device Management
				<i className="pi pi-angle-right" />
				Onboarding
			</p>
            <div className="row" data-testid="onboarding-container-dropdown">
                <div className="col-12 col-lg-2">
                    <label>Device Type</label>
                </div>
                <div className="col-12 col-lg-10">
                    <Dropdown value={selectedDeviceType} options={deviceTypes} optionLabel="name" onChange={deviceTypeChange} 
                        className="input" placeholder="Select device type to onboard" data-testid="onboarding-dropdown" />
                </div>
            </div>
            { selectedDeviceType?.deviceTypeId === 17 &&
                (
                    <div className="row">
                        <div className="col-12 col-lg-2">
                            <label htmlFor="processName">Process Name</label>
                        </div>
                        <div className="col-12 col-lg-10">
                            <Dropdown id="processName" value={selectedProcessName} options={processNames} 
                                onChange={(e) => setSelectedProcessName(e.value)} 
                                className="input" placeholder="Select process name" data-testid="processname-dropdown" />
                        </div>
                    </div>
                )
            }
            { selectedDeviceType?.deviceTypeId===10 &&
                (
                    <>
                        <div className="row">
                            <div className="col-12 col-lg-2">
                                <label htmlFor="snowTicket">Snow Ticket #</label>
                            </div>
                            <div className="col-12 col-lg-10">
                                <InputText id="snowTicket" value={snowTicketNumber} onChange={e => setSnowTicketNumber(e.target.value)} />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12 col-lg-2">
                                <label htmlFor="description">Description</label>
                            </div>
                            <div className="col-12 col-lg-10">
                                <InputText id="description" value={description} onChange={e=> setDescription(e.target.value)} />
                            </div>
                        </div>
                    </>
                )
            }
            <div className="bulk-upload" data-testid="bulk-upload-container">
                {showResultMessage &&
                    <div className="bulk-upload-success">
                        <div>{selectedFileName}</div>
                        <span>{resultMessage}</span>
                    </div>
                }
                {
                    fileUploadError && fileUploadError.map((error) => {
                        return <div className="error" key={error}>{error}</div>
                    })
                }
                <div className="url-container">
                    { selectedDeviceType && <a className="download-template" href={downloadTemplateUrl} download>Download Template</a> }
                    { successMessage && <a className="success-url" href={successMessage}>Download Output File</a> }
                </div>
                <FileUpload ref={fileInputRef} name="file" className="bulk-upload-button" customUpload={true} onSelect={onFileSelect} uploadHandler={onFileUpload} accept=".xls, .xlsx" disabled={!selectedDeviceType}
                data-pc-section="choosebuttonlabel"aria-label="Choose file to upload"
                    emptyTemplate={<div><span className="or-text">or</span><p className="p-m-0"><img src={fileUpload} alt="" /> Drag and Drop Excel File</p></div>} />
            </div>

            <Dialog role="dialog" header="Alert" visible={displayDialog} style={{ width: '30vw' }} onHide={() => setDisplayDialog(false)}>
                You can only upload Excel file with maximum size of 2MB
            </Dialog>

            <hr />
            <DeviceOnboardingLogs />
        </div>
    );
}

export default DeviceOnboarding;